import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { hexToRgba } from '../../../utils/colors.helper'
import { useThemeColors } from '../../../hooks/useThemeColors'

/**
 * HighlightScrollbar - Displays highlight position indicators on a custom scrollbar
 * Similar to VSCode's scrollbar highlight markers
 */
const HighlightScrollbar = ({ scrollableRef, highlightRanges, highlightAnchorsRef, onMarkerClick, currentIndex, colors = {} }) => {
    const [markers, setMarkers] = useState([])
    const [hoveredIndex, setHoveredIndex] = useState(null)
    const themeColors = useThemeColors()

    useEffect(() => {
        const scrollableEl = scrollableRef.current
        if (!scrollableEl || !Array.isArray(highlightRanges) || highlightRanges.length === 0) {
            setMarkers([])
            return
        }

        const updateMarkers = () => {
            const containerHeight = scrollableEl.clientHeight
            const scrollHeight = scrollableEl.scrollHeight

            // Only show markers when content is scrollable
            if (scrollHeight === 0 || containerHeight === 0 || scrollHeight <= containerHeight) {
                setMarkers([])
                return
            }

            const newMarkers = []
            highlightRanges.forEach((range, index) => {
                if (!range || typeof range.start !== 'number' || typeof range.end !== 'number') {
                    return
                }

                const anchorRef = highlightAnchorsRef.current[index]
                if (!anchorRef || !anchorRef.current) {
                    return
                }

                const anchor = anchorRef.current
                const anchorTop = anchor.offsetTop
                const anchorHeight = anchor.offsetHeight || 0

                // Scale the position to the visible scrollbar area
                const markerTop = (anchorTop / scrollHeight) * containerHeight
                const markerHeight = Math.max(4, (anchorHeight / scrollHeight) * containerHeight * 2) // 2x height

                newMarkers.push({
                    index,
                    top: markerTop,
                    height: markerHeight,
                    color: range.color || colors.warning || themeColors.warning,
                })
            })

            setMarkers(newMarkers)
        }

        updateMarkers()
        const ro = new ResizeObserver(updateMarkers)
        ro.observe(scrollableEl)

        // Also observe when content changes
        const contentObserver = new MutationObserver(updateMarkers)
        contentObserver.observe(scrollableEl, { childList: true, subtree: true })

        return () => {
            ro.disconnect()
            contentObserver.disconnect()
        }
    }, [scrollableRef, highlightRanges, highlightAnchorsRef])

    // Don't render if there are no markers (which means no scroll or no highlights)
    if (markers.length === 0) {
        return null
    }

    const handleMarkerClick = (index) => {
        if (onMarkerClick) {
            onMarkerClick(index)
        }
    }

    return (
        <div
            style={{
                position: 'absolute',
                right: 0,
                top: 0,
                width: '15px',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 5,
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            {markers.map(marker => {
                const isHovered = hoveredIndex === marker.index
                const isCurrent = currentIndex === marker.index
                
                return (
                    <div
                        key={`highlight-${marker.index}`}
                        onClick={() => handleMarkerClick(marker.index)}
                        onMouseEnter={() => setHoveredIndex(marker.index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        style={{
                            position: 'absolute',
                            top: `${marker.top}px`,
                            width: isHovered ? '12px' : isCurrent ? '10px' : '9px', // 1.5x wider
                            height: `${marker.height}px`,
                            backgroundColor: marker.color,
                            borderRadius: '4px',
                            opacity: isHovered ? 1 : isCurrent ? 0.95 : 0.85,
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                            zIndex: isHovered ? 12 : isCurrent ? 11 : 10,
                            transition: 'all 0.15s ease',
                            border: isCurrent 
                                ? `1px solid ${colors.text ? hexToRgba(colors.text, 0.5) : 'rgba(0, 0, 0, 0.2)'}` 
                                : 'none',
                            boxShadow: isHovered 
                                ? `0 0 4px ${colors.text ? hexToRgba(colors.text, 0.3) : 'rgba(0, 0, 0, 0.3)'}`
                                : isCurrent 
                                ? `0 0 2px ${colors.text ? hexToRgba(colors.text, 0.2) : 'rgba(0, 0, 0, 0.2)'}` 
                                : 'none',
                        }}
                        title={`Jump to highlight ${marker.index + 1}`}
                    />
                )
            })}
        </div>
    )
}

HighlightScrollbar.propTypes = {
    scrollableRef: PropTypes.object.isRequired,
    highlightRanges: PropTypes.arrayOf(
        PropTypes.shape({
            start: PropTypes.number.isRequired,
            end: PropTypes.number.isRequired,
            color: PropTypes.string,
            textColor: PropTypes.string,
            opacity: PropTypes.number,
        }),
    ),
    highlightAnchorsRef: PropTypes.object.isRequired,
    onMarkerClick: PropTypes.func,
    currentIndex: PropTypes.number,
    colors: PropTypes.object,
}

export default HighlightScrollbar

